import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { KOINONIA } from "@/constants/testIds";
import { toast, confirmToast } from "@/lib/notify";
import {
  Plus,
  Pencil,
  Trash2,
  Coffee,
  TrendingUp,
  TrendingDown,
  Wallet,
  Download,
  AlertCircle,
  X,
  Users,
  ArrowRight,
} from "lucide-react";

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Koinonia() {
  const [tab, setTab] = useState("movements");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  return (
    <div data-testid={KOINONIA.page} className="p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-[#1c1a14] flex items-center gap-2">
          <Coffee className="text-[#c8a04a]" /> Koinonía Café
        </h1>
        <p className="text-[13px] text-[#8b7e5e] mt-1">
          Caja chica independiente. No afecta los registros de diezmos y ofrendas.
        </p>
      </div>

      <SummaryHeader year={year} month={month} />

      <div className="mb-5 flex gap-2 border-b border-[#d9cfb8]">
        <Tab active={tab === "movements"} onClick={() => setTab("movements")} testId={KOINONIA.tabMovements}>
          Movimientos
        </Tab>
        <Tab active={tab === "groups"} onClick={() => setTab("groups")} testId={KOINONIA.tabGroups}>
          Grupos
        </Tab>
        <Tab active={tab === "summary"} onClick={() => setTab("summary")} testId={KOINONIA.tabSummary}>
          Resumen & Reporte
        </Tab>
      </div>

      {tab === "movements" && (
        <MovementsPanel year={year} month={month} setYear={setYear} setMonth={setMonth} />
      )}
      {tab === "groups" && <GroupsPanel />}
      {tab === "summary" && <SummaryPanel year={year} month={month} setYear={setYear} setMonth={setMonth} />}
    </div>
  );
}

function Tab({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-2 text-[12px] uppercase tracking-widest border-b-2 -mb-px ${
        active
          ? "border-[#c8a04a] text-[#1c1a14]"
          : "border-transparent text-[#8b7e5e] hover:text-[#1c1a14]"
      }`}
    >
      {children}
    </button>
  );
}

function SummaryHeader({ year, month }) {
  const [data, setData] = useState({ total_entradas: 0, total_salidas: 0, balance_all_time: 0 });
  useEffect(() => {
    api
      .get("/koinonia/summary", { params: { year, month } })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [year, month]);
  return (
    <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
      <StatCard
        testId={KOINONIA.statEntradas}
        icon={TrendingUp}
        label={`Entradas (${MONTHS_ES[month - 1]} ${year})`}
        value={fmt(data.total_entradas)}
        color="emerald"
      />
      <StatCard
        testId={KOINONIA.statSalidas}
        icon={TrendingDown}
        label={`Salidas (${MONTHS_ES[month - 1]} ${year})`}
        value={fmt(data.total_salidas)}
        color="red"
      />
      <StatCard
        testId={KOINONIA.statBalance}
        icon={Wallet}
        label="Saldo acumulado total"
        value={fmt(data.balance_all_time)}
        color="navy"
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    emerald: "border-emerald-300 bg-emerald-50/50 text-emerald-800",
    red: "border-red-300 bg-red-50/50 text-red-800",
    navy: "border-[#1c2a3a] bg-[#0f1a2c] text-[#e7c879]",
  };
  return (
    <div data-testid={testId} className={`rounded border px-4 py-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
        <Icon size={14} /> {label}
      </div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}

// =========== MOVEMENTS ===========
function MovementsPanel({ year, month, setYear, setMonth }) {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [m, g] = await Promise.all([
        api.get("/koinonia/movements", { params: { year, month } }),
        api.get("/koinonia/groups"),
      ]);
      setRows(m.data);
      setGroups(g.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando movimientos");
    }
  };

  useEffect(() => {
    load();
  }, [year, month]);

  const remove = async (m) => {
    if (!confirm("¿Eliminar movimiento?")) return;
    await api.delete(`/koinonia/movements/${m.id}`);
    load();
  };

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          <AlertCircle size={14} /> {error}
          <button onClick={() => setError("")} className="ml-auto text-red-500 hover:text-red-700"><X size={14} /></button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <select
            data-testid={KOINONIA.monthSelect}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
          >
            {MONTHS_ES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            data-testid={KOINONIA.yearSelect}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <button
          data-testid={KOINONIA.addMovementButton}
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
        >
          <Plus size={14} /> Nuevo movimiento
        </button>
      </div>

      {!rows.length ? (
        <div className="rounded border border-dashed border-[#d9cfb8] bg-white p-10 text-center text-[#8b7e5e]">
          No hay movimientos registrados en {MONTHS_ES[month - 1]} {year}.
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-[#d9cfb8] bg-white">
          <table data-testid={KOINONIA.movementsTable} className="w-full text-[13px]">
            <thead className="bg-[#0f1a2c] text-[#e7c879]">
              <tr>
                <Th>Fecha</Th><Th>Tipo</Th><Th>Aportante</Th><Th>Descripción</Th>
                <Th className="text-right">Monto</Th><Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} data-testid={KOINONIA.movementRow(m.id)} className="border-t border-[#ebe2c9] hover:bg-[#fbf7ee]">
                  <Td>{m.date}</Td>
                  <Td>
                    {m.type === "entrada" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-emerald-400 bg-emerald-50 text-emerald-800 rounded px-2 py-0.5">
                        <TrendingUp size={11} /> Entrada
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-red-400 bg-red-50 text-red-800 rounded px-2 py-0.5">
                        <TrendingDown size={11} /> Salida
                      </span>
                    )}
                  </Td>
                  <Td>
                    <div className="text-[#1c1a14]">{m.contributor_name || "—"}</div>
                    <div className="text-[10px] text-[#8b7e5e]">
                      {m.contributor_type === "grupo" ? "Grupo" : "Persona"}
                    </div>
                  </Td>
                  <Td>{m.description || "—"}</Td>
                  <Td className={`text-right font-medium ${m.type === "entrada" ? "text-emerald-700" : "text-red-700"}`}>
                    {m.type === "entrada" ? "+" : "-"}{fmt(m.amount)}
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <button
                        data-testid={KOINONIA.movementEditButton(m.id)}
                        onClick={() => { setEditing(m); setShowForm(true); }}
                        className="text-[#5d5849] hover:text-[#1c1a14] p-1"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={KOINONIA.movementDeleteButton(m.id)}
                        onClick={() => remove(m)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <MovementForm
          initial={editing}
          groups={groups}
          onClose={() => setShowForm(false)}
          onSaved={(saved) => {
            setShowForm(false);
            // Jump month/year selectors to the saved movement date so user sees the new row
            if (saved && saved.date) {
              const [y, m] = saved.date.split("-").map(Number);
              if (y) setYear(y);
              if (m) setMonth(m);
            }
            load();
          }}
        />
      )}
    </div>
  );
}

function MovementForm({ initial, groups, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      date: todayStr(),
      type: "entrada",
      amount: "",
      description: "",
      contributor_type: "persona",
      contributor_name: "",
      group_id: "",
      notes: "",
    },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.amount) <= 0) {
      setErr("El monto debe ser mayor a 0");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = { ...form, amount: Number(form.amount) };
      let saved;
      if (initial) {
        const r = await api.put(`/koinonia/movements/${initial.id}`, body);
        saved = r.data;
      } else {
        const r = await api.post("/koinonia/movements", body);
        saved = r.data;
      }
      onSaved(saved);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  // When choosing a group, auto-fill contributor_name
  const onGroupChange = (gid) => {
    update("group_id", gid);
    const g = groups.find((x) => x.id === gid);
    if (g) {
      update("contributor_name", g.name);
      update("contributor_type", "grupo");
    }
  };

  return (
    <Modal title={initial ? "Editar movimiento" : "Nuevo movimiento"} onClose={onClose} testId={KOINONIA.movementModal}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha *">
            <input
              data-testid={KOINONIA.movementDateInput}
              type="date"
              value={form.date}
              onChange={(e) => update("date", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              required
            />
          </Field>
          <Field label="Tipo *">
            <select
              data-testid={KOINONIA.movementTypeSelect}
              value={form.type}
              onChange={(e) => update("type", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              required
            >
              <option value="entrada">Entrada</option>
              <option value="salida">Salida</option>
            </select>
          </Field>
          <Field label="Monto ($) *">
            <input
              data-testid={KOINONIA.movementAmountInput}
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              required
            />
          </Field>
          <Field label="Tipo de aportante">
            <select
              data-testid={KOINONIA.movementContribTypeSelect}
              value={form.contributor_type}
              onChange={(e) => update("contributor_type", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            >
              <option value="persona">Persona</option>
              <option value="grupo">Grupo</option>
            </select>
          </Field>
          {form.contributor_type === "grupo" ? (
            <Field label="Grupo">
              <select
                data-testid={KOINONIA.movementGroupSelect}
                value={form.group_id}
                onChange={(e) => onGroupChange(e.target.value)}
                className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
              >
                <option value="">Seleccionar grupo...</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label="Nombre del aportante">
              <input
                data-testid={KOINONIA.movementContribNameInput}
                value={form.contributor_name}
                onChange={(e) => update("contributor_name", e.target.value)}
                className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
                placeholder="Nombre completo"
              />
            </Field>
          )}
        </div>

        <Field label="Descripción">
          <input
            data-testid={KOINONIA.movementDescInput}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            placeholder="Ej: Venta de café del domingo, compra de azúcar, etc."
          />
        </Field>
        <Field label="Notas">
          <textarea
            data-testid={KOINONIA.movementNotesInput}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={2}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          />
        </Field>

        {err && (
          <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid={KOINONIA.movementCancelButton}
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={KOINONIA.movementSaveButton}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =========== GROUPS ===========
function GroupsPanel() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/koinonia/groups");
      setRows(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error cargando grupos");
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (g) => {
    const ok = await confirmToast(`¿Eliminar grupo "${g.name}"?`, {
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await api.delete(`/koinonia/groups/${g.id}`);
    load();
  };

  const rotating = rows.filter((g) => !g.is_collaborator);
  const collab = rows.filter((g) => g.is_collaborator);

  return (
    <div>
      {err && (
        <div className="mb-4 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          <AlertCircle size={14} /> {err}
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <button
          data-testid={KOINONIA.addGroupButton}
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a]"
        >
          <Plus size={14} /> Nuevo grupo
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6" data-testid={KOINONIA.groupsList}>
        <GroupList
          title="Grupos rotativos (mensuales)"
          subtitle="4 grupos que aportan por turnos cada mes"
          icon={ArrowRight}
          items={rotating}
          onEdit={(g) => { setEditing(g); setShowForm(true); }}
          onDelete={remove}
        />
        <GroupList
          title="Grupos colaboradores"
          subtitle="Ministerios y personas que también aportan"
          icon={Users}
          items={collab}
          onEdit={(g) => { setEditing(g); setShowForm(true); }}
          onDelete={remove}
        />
      </div>

      {showForm && (
        <GroupForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function GroupList({ title, subtitle, icon: Icon, items, onEdit, onDelete }) {
  return (
    <div className="rounded border border-[#d9cfb8] bg-white p-4">
      <div className="mb-3">
        <h3 className="font-serif text-lg text-[#1c1a14] flex items-center gap-2">
          <Icon size={16} className="text-[#c8a04a]" /> {title}
        </h3>
        <p className="text-[11px] text-[#8b7e5e]">{subtitle}</p>
      </div>
      {!items.length ? (
        <p className="text-[12px] text-[#8b7e5e] italic">No hay grupos.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((g) => (
            <li
              key={g.id}
              data-testid={KOINONIA.groupRow(g.id)}
              className="rounded border border-[#ebe2c9] bg-[#fbf7ee] px-3 py-2 flex items-start justify-between gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1c1a14] text-[14px]">{g.name}</div>
                {g.rotation_month && (
                  <div className="text-[10px] text-[#8b7e5e]">
                    Mes asignado: {MONTHS_ES[g.rotation_month - 1]}
                  </div>
                )}
                {g.members && g.members.length > 0 && (
                  <div className="text-[11px] text-[#5d5849] mt-1">
                    <span className="text-[#8b7e5e]">Miembros ({g.members.length}):</span>{" "}
                    {g.members.join(", ")}
                  </div>
                )}
                {g.notes && (
                  <div className="text-[11px] text-[#8b7e5e] mt-1 italic">{g.notes}</div>
                )}
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={KOINONIA.groupEditButton(g.id)}
                  onClick={() => onEdit(g)}
                  className="text-[#5d5849] hover:text-[#1c1a14] p-1"
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={KOINONIA.groupDeleteButton(g.id)}
                  onClick={() => onDelete(g)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      name: "",
      is_collaborator: false,
      rotation_month: "",
      members: [],
      notes: "",
    },
  );
  const [membersText, setMembersText] = useState(
    (initial?.members || []).join("\n"),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = {
        name: form.name,
        is_collaborator: !!form.is_collaborator,
        rotation_month: form.rotation_month ? Number(form.rotation_month) : null,
        members: membersText.split("\n").map((s) => s.trim()).filter(Boolean),
        notes: form.notes || "",
      };
      if (initial) {
        await api.put(`/koinonia/groups/${initial.id}`, body);
      } else {
        await api.post("/koinonia/groups", body);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={initial ? "Editar grupo" : "Nuevo grupo"} onClose={onClose} testId={KOINONIA.groupModal}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nombre del grupo *">
          <input
            data-testid={KOINONIA.groupNameInput}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            required
          />
        </Field>
        <label className="flex items-center gap-2 text-[13px]">
          <input
            data-testid={KOINONIA.groupCollaboratorToggle}
            type="checkbox"
            checked={!!form.is_collaborator}
            onChange={(e) => update("is_collaborator", e.target.checked)}
            className="accent-[#c8a04a]"
          />
          Es un grupo colaborador (ministerios, personas externas)
        </label>
        {!form.is_collaborator && (
          <Field label="Mes asignado (rotación mensual)">
            <select
              data-testid={KOINONIA.groupRotationSelect}
              value={form.rotation_month || ""}
              onChange={(e) => update("rotation_month", e.target.value)}
              className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
            >
              <option value="">Sin mes asignado</option>
              {MONTHS_ES.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Integrantes (uno por línea, hasta 5 sugerido)">
          <textarea
            data-testid={KOINONIA.groupMembersInput}
            value={membersText}
            onChange={(e) => setMembersText(e.target.value)}
            rows={5}
            placeholder={"Nombre 1\nNombre 2\nNombre 3"}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded font-mono"
          />
        </Field>
        <Field label="Notas">
          <textarea
            data-testid={KOINONIA.groupNotesInput}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={2}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded"
          />
        </Field>

        {err && (
          <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid={KOINONIA.groupCancelButton}
            onClick={onClose}
            disabled={busy}
            className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid={KOINONIA.groupSaveButton}
            disabled={busy}
            className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
          >
            {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =========== SUMMARY / REPORT ===========
function SummaryPanel({ year, month, setYear, setMonth }) {
  const [summary, setSummary] = useState(null);
  const [scope, setScope] = useState("monthly"); // monthly | annual

  const load = async () => {
    const params = { year };
    if (scope === "monthly") params.month = month;
    const r = await api.get("/koinonia/summary", { params });
    setSummary(r.data);
  };
  useEffect(() => { load(); }, [year, month, scope]);

  const downloadPdf = async () => {
    const params = new URLSearchParams({ year: String(year) });
    if (scope === "monthly") params.append("month", String(month));
    const res = await fetch(`${API}/koinonia/report.pdf?${params.toString()}`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("ajar_token") || ""}`,
      },
    });
    if (!res.ok) {
      toast.error("Error generando PDF");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = scope === "monthly"
      ? `koinonia-cafe-${MONTHS_ES[month - 1]}-${year}.pdf`
      : `koinonia-cafe-Anual-${year}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!summary) return <div className="p-6 text-[#8b7e5e]">Cargando...</div>;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex gap-2">
          <Field label="Alcance">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
            >
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
            </select>
          </Field>
          {scope === "monthly" && (
            <Field label="Mes">
              <select
                data-testid={KOINONIA.summaryMonthSelect}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
              >
                {MONTHS_ES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </Field>
          )}
          <Field label="Año">
            <select
              data-testid={KOINONIA.summaryYearSelect}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-[#d9cfb8] bg-white px-3 py-1.5 text-[13px] rounded"
            >
              {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
        </div>
        <button
          data-testid={KOINONIA.summaryDownloadPdf}
          onClick={downloadPdf}
          className="inline-flex items-center gap-2 rounded border border-[#1c2a3a] bg-[#0f1a2c] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#e7c879] hover:bg-[#1c2a3a]"
        >
          <Download size={14} /> Descargar reporte PDF
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={TrendingUp} label="Entradas" value={fmt(summary.total_entradas)} color="emerald" />
        <StatCard icon={TrendingDown} label="Salidas" value={fmt(summary.total_salidas)} color="red" />
        <StatCard icon={Wallet} label="Neto del período" value={fmt(summary.net)} color="navy" />
        <StatCard icon={Wallet} label="Saldo histórico" value={fmt(summary.balance_all_time)} color="navy" />
      </div>

      {scope === "annual" && summary.by_month && summary.by_month.length > 0 && (
        <div className="rounded border border-[#d9cfb8] bg-white p-4 mb-6">
          <h3 className="font-serif text-lg text-[#1c1a14] mb-3">Resumen mensual {year}</h3>
          <table className="w-full text-[13px]">
            <thead className="text-[#8b7e5e] text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left py-1">Mes</th>
                <th className="text-right py-1">Entradas</th>
                <th className="text-right py-1">Salidas</th>
                <th className="text-right py-1">Neto</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_month.map((r) => (
                <tr key={r.month} className="border-t border-[#ebe2c9]">
                  <td className="py-1.5">{r.label}</td>
                  <td className="py-1.5 text-right text-emerald-700">{fmt(r.entradas)}</td>
                  <td className="py-1.5 text-right text-red-700">{fmt(r.salidas)}</td>
                  <td className="py-1.5 text-right font-medium">{fmt(r.entradas - r.salidas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary.contributors && summary.contributors.length > 0 && (
        <div className="rounded border border-[#d9cfb8] bg-white p-4">
          <h3 className="font-serif text-lg text-[#1c1a14] mb-3">Aportantes</h3>
          <table className="w-full text-[13px]">
            <thead className="text-[#8b7e5e] text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left py-1">Nombre</th>
                <th className="text-left py-1">Tipo</th>
                <th className="text-right py-1">Aportes</th>
                <th className="text-right py-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.contributors.map((c) => (
                <tr key={c.name} className="border-t border-[#ebe2c9]">
                  <td className="py-1.5">{c.name}</td>
                  <td className="py-1.5 capitalize">{c.type}</td>
                  <td className="py-1.5 text-right">{c.count}</td>
                  <td className="py-1.5 text-right font-medium">{fmt(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ shared ============
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={testId}
        className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded border border-[#d9cfb8] bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#1c1a14]">{title}</h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-widest font-normal text-left ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 ${className}`}>{children}</td>
);
